<template>
  <span v-if="displayLabel">{{ placeholder }}</span>
  <dx-drop-down-box
    ref="dropDownBox"
    :data-source="dataSource"
    :defer-rendering="false"
    :display-expr="displayExpr"
    :drop-down-options="{ width: dropDownWidth }"
    :field-template="((displayTotal || validateRequired) && 'field') || undefined"
    :placeholder="displayPlaceholder && placeholder"
    :show-clear-button="showClearButton"
    :value="selectedKeys"
    :value-expr="keyExpr"
    :width="'100%'"
    @value-changed="dropDownBoxValueChanged($event)"
  >

    <template #field>
      <div class="d-flex align-items-center">
        <DxTextBox
          :placeholder="displayPlaceholder && placeholder"
          :read-only="true"
          :value="displayText"
        />
        <div v-if="showValidation">
          <i id="errorIconDepartment" class="dx-icon dx-icon-info text-danger" />
          <dx-tooltip
            :hide-on-outside-click="false"
            :target="'#errorIconDepartment'"
            hide-event="mouseleave"
            position="right"
            show-event="mouseenter"
          >
            Необходимо заполнить!
          </dx-tooltip>
        </div>
      </div>
    </template>

    <template>
      <dx-data-grid v-if="!parentIdExpr"
                    ref="dataGridRef"
                    v-model:selected-row-keys="selectedKeys"
                    :columns="columns"
                    :data-source="dataSource"
                    :filter-row="{ visible: showFilterRow }"
                    :focused-row-enabled="false"
                    :height="dropDownHeight"
                    :hover-state-enabled="true"
                    :key-expr="keyExpr"
                    :show-column-headers="showColumnHeaders"
                    @contentReady="onContentReady($event)"
                    @selectionChanged="selectionChanged($event)"
      >
        <dx-selection :mode="selectionMode" />
        <dx-scrolling mode="virtual" />
      </dx-data-grid>
      <dx-tree-list v-if="parentIdExpr"
                    ref="treeListRef"
                    v-model:selected-row-keys="selectedKeys"
                    :columns="columns"
                    :data-source="dataSource"
                    :filter-row="{ visible: showFilterRow }"
                    :focused-row-enabled="false"
                    :height="dropDownHeight"
                    :hover-state-enabled="true"
                    :key-expr="keyExpr"
                    :parent-id-expr="parentIdExpr"
                    :root-value="rootValue || null"
                    :show-column-headers="showColumnHeaders"
                    @contentReady="onContentReady($event)"
                    @selection-changed="selectionChanged($event)"
      >
        <dx-selection :mode="selectionMode" />
        <dx-scrolling mode="virtual" />
      </dx-tree-list>
    </template>
  </dx-drop-down-box>
</template>

<script>
export default {
  name: 'ai-drop-down-tab',
};
</script>

<script setup>
import { onMounted, defineEmits, defineModel, defineProps, ref, computed, defineExpose } from 'vue';
import {
  DxDataGrid,
  DxScrolling,
  DxSelection,
} from 'devextreme-vue/data-grid';
import {
  DxTreeList,
} from 'devextreme-vue/tree-list';
import DxDropDownBox from 'devextreme-vue/drop-down-box';
import { DxTextBox, DxTooltip } from 'devextreme-vue';
import { expandTree } from '@/components/utils/ai-data-source-generator';

const value = defineModel('value');
const dropDownBox = ref();
const selectedKeys = ref([]);
const dataSource = ref([]);

const emit = defineEmits(['valueChanged']);

const dataGridRef = ref(),
  treeListRef = ref();
let selectedData = ref([]);
let isAfterLoad = false;
let allIds = [];

const {
  columns,
  selectionMode,
  chooseAll,
  showClearButton,
  placeholder,
  keyExpr,
  dropDownWidth,
  getDataSource,
  valueExpr,
  parentIdExpr,
  arrayDataSource,
  dropDownHeight,
  defaultValue,
  showColumnHeaders,
  showFilterRow,
  rootValue,
  validateRequired,
  displayTotal,
  displayLabel,
  displayPlaceholder,
} = defineProps({
  dropDownHeight: {
    type: String,
    default: '200px',
  },
  chooseAll: {
    type: Boolean,
    default: false,
  },
  displayTotal: {
    type: Boolean,
    default: false,
  },
  displayLabel: {
    type: Boolean,
    default: true,
  },
  displayPlaceholder: {
    type: Boolean,
    default: true,
  },
  selectionMode: {
    type: String,
    default: 'multiple',
  },
  showClearButton: {
    type: Boolean,
    default: true,
  },
  showColumnHeaders: {
    type: Boolean,
    default: true,
  },
  showFilterRow: {
    type: Boolean,
    default: true,
  },
  validateRequired: {
    type: Boolean,
    default: false,
  },
  getDataSource: Function,
  arrayDataSource: Array,
  defaultValue: Number,
  placeholder: String,
  columns: Array,
  dropDownWidth: Number,
  keyExpr: String,
  valueExpr: String,
  parentIdExpr: String,
  rootValue: String,
});

const displayText = computed({
  get: () => {
    return displayTotal && `Выбрано: ${selectedKeys.value.length}`
      || selectionMode === 'single' && selectedData.value.length && selectedData.value[0].name
      || null;
  },
});

onMounted(async () => {
  dataSource.value = arrayDataSource || await getDataSource();
  isAfterLoad = true;
  if (parentIdExpr)
    allIds = dataSource.value.map(x => x.id);
  if (![null, undefined].includes(defaultValue)) {
    if (Array.isArray(defaultValue))
      selectedKeys.value = [...defaultValue];
    else
      selectedKeys.value.push(defaultValue);
  } else if (chooseAll) {
    selectedKeys.value = dataSource.value.map(x => x[keyExpr]);
  }
});

const selectionChanged = (e) => {
  const ids = e.selectedRowsData.map(x => x[keyExpr]);
  value.value = ids;
  selectedData.value = e.selectedRowsData;

  if (selectionMode === 'single') {
    dropDownBox.value.instance.close();
    emit('valueChanged', ids[0] === 0 ? 0 : ids[0] || null);
  } else {
    emit('valueChanged', ids);
  }
};

const onContentReady = (e) => {
  if (isAfterLoad && parentIdExpr && allIds.length) {
    expandTree({
      ids: allIds,
      dxInstance: getInstance(),
    });
  }
  isAfterLoad = false;
};

const getInstance = () => {
  return !parentIdExpr ? dataGridRef.value.instance : treeListRef.value.instance;
};

const showValidation = computed({
  get: () => validateRequired && !value.value,
});

const dropDownBoxValueChanged = (e) => {
  if (!e.value) {
    value.value = [];
    selectedKeys.value = [];
  }
};

const displayExpr = (item) => {
  return item && item[valueExpr] || null;
};

const refreshData = (data) => {
  dataSource.value = data;
  isAfterLoad = true;
  if (parentIdExpr)
    allIds = dataSource.value.map(x => x.id);
};

defineExpose({ refreshData });
</script>

<style scoped>

</style>
